<template>
  <div>
    <div class="d-flex justify-content-between" v-if="showColors">
      <label>{{ colorsCount }} {{ selectedPart.partGroupDescription }}</label>
    </div>
    <div>
      <DropdownButton
        :id="selectedPart.partId"
        label="Select part..."
        :customButton="!dropdownButton"
        @remove="$emit('choosePart', null)"
        class="position-relative"
      >
        <template v-slot:customButton>
          <button
            type="button"
            :id="`submenu-${selectedPart.partId}`"
            data-toggle="dropdown"
            @click="loadData"
            class="btn bg-white w-100 border pointer"
          >
            <PartsDropdownOption :showSizes="showSizes" :selectedPart="selectedPart" />
          </button>
        </template>
        <template v-slot:menu>
          <div class="dropdown-menu w-100 px-2" role="menu" :aria-labelledby="`submenu-${selectedPart.partId}`">
            <input type="text" v-model="partsSearch" class="form-control mr-2 mb-2" placeholder="Search parts..." />
            <ul class="list-group" style="max-height: 400px; overflow-y: scroll">
              <li class="d-flex justify-content-between cursor-pointer p-2 border-bottom" v-if="loading">
                <loading />
              </li>
              <li
                v-else
                role="presentation"
                v-for="part in filteredPartsList"
                class="d-flex justify-content-between cursor-pointer p-2 border-bottom"
                @click="$emit('choosePart', part)"
                :key="`${part.partId}-${part.updatedAt}`"
              >
                <PartsDropdownOption :showSizes="showSizes" :selectedPart="part" />
              </li>
            </ul>
          </div>
        </template>
      </DropdownButton>
    </div>
  </div>
</template>

<script>
import { mediaHasClassTypeName } from '@/utils/media';
import { encodeOpts } from '../utils/encode-opts';

function parseParts(parts, isDecoratedProduct) {
  const uniqueColors = new Map();

  parts.forEach((part) => {
    if (part && part.mediaList) {
      part.image =
        part.mediaList.find((img) => part?.imageDefault?._id === img._id) ||
        part.mediaList.find((img) => mediaHasClassTypeName(img, 'Primary')) ||
        part.mediaList.find((img) => img.partId === part.partId && img.isDefault) ||
        part.mediaList.find((img) => img.partId === part.partId) ||
        part.mediaList[0];
    }

    const colorName = !isDecoratedProduct
      ? part?.colors?.length
        ? part.colors.map((color) => color.name).join('/')
        : part?.partId
      : part?.partId;

    if (uniqueColors.has(colorName)) {
      const existingPart = uniqueColors.get(colorName);
      existingPart.mediaList = [...existingPart.mediaList, ...part.mediaList];
    } else {
      uniqueColors.set(colorName, { ...part, colorName, mediaList: part.mediaList });
    }
  });

  return Array.from(uniqueColors.values());
}
export default {
  data() {
    return {
      partsSearch: '',
      loading: false,
      partsList: []
    };
  },
  name: 'PartsDropdown',
  props: {
    dropdownButton: {
      type: Boolean,
      default: false
    },
    isDecoratedProduct: {
      type: Boolean,
      default: false
    },
    showColors: {
      type: Boolean,
      default: true
    },
    showPricing: {
      type: Boolean,
      default: true
    },
    showSizes: {
      type: Boolean,
      default: true
    },
    part: {
      type: Object,
      required: true
    },
    parts: {
      type: Array,
      default: () => []
    },
    productId: {
      type: String
    }
  },
  created() {
    this.partsList = parseParts(this.parts, this.isDecoratedProduct);
  },
  computed: {
    filteredPartsList() {
      if (!this.partsSearch) return this.partsList;

      const search = this.partsSearch.toLowerCase();
      if (!this.isDecoratedProduct) {
        const uniqueColors = new Map();

        this.partsList.forEach((part) => {
          const colorName = part.colorName.toLowerCase();
          if (colorName.includes(search) && part.image) {
            uniqueColors.set(colorName, part);
          }
        });

        return Array.from(uniqueColors.values());
      } else {
        return this.partsList.filter(
          (part) =>
            part?.partDescription?.toLowerCase().includes(search) ||
            part?.partId?.toLowerCase().includes(search) ||
            part?.partGroupDescription?.toLowerCase().includes(search)
        );
      }
    },
    colorsCount() {
      return this.parts.filter((i) => !i.isNone).length;
    },
    selectedPart() {
      let selectedPart = this.partsList.find((i) => i.partId === this.part.partId);
      if (!selectedPart) {
        const color = this.part.colorName || (this.part.colors && this.part.colors.map((e) => e.name).join(','));
        if (color) selectedPart = this.partsList.find((i) => i.colorName === color);
        if (selectedPart && this.part.ApparelSize)
          selectedPart = { ...JSON.parse(JSON.stringify(selectedPart)), ApparelSize: this.part.ApparelSize };
      }
      return selectedPart || {};
    }
  },
  methods: {
    async loadData() {
      try {
        if (this.loading) return;
        this.loading = true;
        const existPartIds = this.partsList.map((e) => e.partId);

        const data = parseParts(
          (
            await this.$http.get(
              `/products/${typeof this.productId !== 'undefined' ? this.productId : this.parts[0].productId}/parts`,
              {
                params: {
                  project: {
                    partId: 1,
                    colors: 1,
                    partDescription: 1,
                    pricing: 1,
                    partGroup: 1,
                    partGroupDescription: 1,
                    mediaList: 1,
                    imageDefault: 1
                  }
                },
                paramsSerializer: encodeOpts
              }
            )
          ).data,
          this.isDecoratedProduct
        );
        data.forEach((part) => {
          const exist = this.partsList.find((e) => e.partId === part.partId);
          if (exist) {
            ['image'].forEach((e) => {
              exist[e] = part[e];
            });
            exist.updatedAt = Date.now().toString();
          }
        });
        this.partsList.push(...data.filter((e) => !existPartIds.includes(e.partId)));
      } catch (e) {
        this.notifyError('Error loading parts');
      } finally {
        this.loading = false;
      }
    },
    partPricing(part) {
      const pricing = part.pricing || [];
      return pricing
        .filter((el) => el.unitPrice && el.unitCost && el.configurationType !== 'Blank')
        .map(
          (el) =>
            `<tr>
              <td>${el.minQuantity}</td>
              <td>$${el.unitPrice.toFixed(2)}</td>
              <td>${el.margin}%</td>
              <td>$${el.unitCost.toFixed(2)}</td>
            </tr>`
        )
        .join(' ');
    }
  },
  components: {
    PartsDropdownOption: () => import('./PartsDropdownOption'),
    DropdownButton: () => import('./DropdownButton')
  }
};
</script>
